import { useState } from "react";
import { blurActiveElement } from "@/shared/lib/blurActiveElement";
import { validateLoginForm } from "@/features/auth/lib/authValidation";
import { useReviewLogin } from "@/features/auth/hooks/login/useReviewLogin";
import type { LoginFormErrors, LoginFormValues } from "@/features/auth/model/types";

type UseLoginOptions = {
  /** 로그인 성공 직후(네비게이션 직전) 호출 — 예: 오버레이 시트 닫기. */
  onSuccess?: () => void;
};

type UseLoginReturn = {
  values: LoginFormValues;
  errors: LoginFormErrors;
  isSubmitting: boolean;
  setId: (v: string) => void;
  setPassword: (v: string) => void;
  submit: () => Promise<void>;
};

/** 숨김 ID/PW 로그인 폼 상태·검증. 실행(reviewLogin → 토큰 저장 → 이동 → 에러 처리)은 useReviewLogin 에 위임. */
export function useLogin({ onSuccess }: UseLoginOptions = {}): UseLoginReturn {
  const [values, setValues] = useState<LoginFormValues>({ id: "", password: "" });
  const [errors, setErrors] = useState<LoginFormErrors>({});
  const { isPending, loginWithCredentials } = useReviewLogin();

  const submit = async () => {
    const newErrors = validateLoginForm(values);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // 검증 통과 → 실제 로그인 진행 직전, 직전 입력창의 포커스/키보드 정리.
    // (검증 실패로 화면에 머무는 경우엔 여기 도달하지 않으므로 포커스를 유지한다)
    blurActiveElement();
    await loginWithCredentials(values.id, values.password, onSuccess);
  };

  return {
    values,
    errors,
    isSubmitting: isPending,
    setId: (v) => setValues((prev) => ({ ...prev, id: v })),
    setPassword: (v) => setValues((prev) => ({ ...prev, password: v })),
    submit,
  };
}
